import { type ConditionalDisplayConfig, hiddenLayoutComponentIds } from "@repo/shared";
import type { GoalTypeOption, LocalizedString } from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useUserLanguage } from "@/lib/i18n/context";
import { advisorReviewReasonText } from "./advisor-review-reason-text";
import { PublicFlowComponentRenderer } from "./public-flow-component-renderer";
import { evaluateCondition } from "./public-flow-utils";

/**
 * Everything `PublicPageRenderer` needs that is constant across pages — bundled
 * by the shell (§3.11) and forwarded by each mode body so we don't prop-drill a
 * dozen values through the bodies individually. (`goals` stays loosely typed to
 * match the untyped `usePublicGoals` payload the renderer already accepts.)
 */
export type PublicPageRendererContext = {
  formValues: Record<string, string>;
  goals: any[];
  /** QPBI-395 — flow field id → field type, for type-aware conditional rules. */
  fieldTypes: Record<string, string>;
  isCompleted: boolean;
  flowInstanceId: string;
  riskModelId: string | null;
  portfolioCollectionId: string | null;
  flowCurrency: string;
  multiPortfolio: boolean;
  primaryColor?: string | null;
  productTypes: Array<{ id: string; nameI18n: LocalizedString }>;
  /** QP-187 — the flow collection's goal types (feeds the goal_input type picker). */
  goalTypes: GoalTypeOption[];
  /** QL-281 — whether the flow allows more than one goal (gates the goal list's add button). */
  multiGoal: boolean;
  /** QL-593 — the flow's goal cap while multiGoal is on (gates every add-goal button). */
  maxGoals: number;
  /** QP-185 — the collection's opt-in dynamic setting; gates the "dynamic" type option. */
  dynamicPortfoliosEnabled: boolean;
  /** QL-72 — the collection allows a model portfolio from another risk score. */
  modelDeviationEnabled: boolean;
  portfolioCosts: any[];
  chartColors?: any;
  clientType: "Person" | "Company" | "Group";
  clientName: string;
  language: string;
  /** D14 gating + engine markers (Follow-up C P2) threaded to each component. */
  showValidationErrors: boolean;
  markers: CompletionMarkers;
};

type PublicPageRendererProps = {
  page: any;
  /**
   * The RESOLVED active goal id, used as widget context — the caller (mode body) resolves it:
   *   - single-goal → `goals[0]?.id`
   *   - multi-goal goal page → the iterated goal id
   *   - multi-goal global page → `goals[0]?.id` (widget stopgap; see
   *     INSTANCE_LEVEL_COMPONENT_AUDIT.md)
   * Conditional rules on a global page are evaluated WITHOUT this goal (QL-161).
   */
  goalId: string | null;
  ctx: PublicPageRendererContext;
};

/**
 * Renders a single flow page's title/description + its components, evaluating
 * each component's `conditionalDisplay` against the resolved goal context.
 * Shared by both mode bodies (§3.11; was the inline `renderPagePublic` closure).
 */
export function PublicPageRenderer({ page, goalId, ctx }: PublicPageRendererProps) {
  const activeGoal = ctx.goals.find((g) => g.id === goalId) ?? null;
  // QL-161 — global pages carry no goal scope: their rules are evaluated with no goal in context,
  // matching the advisor wizard and the completion engine.
  const conditionGoalId = page.pageType === "global" ? null : goalId;

  // QL-314 — per-goal advisor-review banner. Shown on goal pages when an
  // advisor-only compliance component (volatility_chart) placed on an internal
  // page blocks this goal. The client sees "advisor action required", never
  // the component or its numbers. Flows without goal pages (or clients on a
  // different page) get the same information from the flow-level alert in
  // PublicFlowContentInner, beside Submit.
  const showAdvisorReviewBanner =
    page.pageType === "goal" &&
    ctx.showValidationErrors &&
    goalId &&
    ctx.markers.missingAdvisorReviewGoalIds?.has(goalId);

  // One line per blocking reason — a goal can be held by both compliance
  // charts at once, and the client must see every one of them. Deduped by
  // text: the not-evaluable reasons of both charts share one line.
  const { userLanguage } = useUserLanguage();
  const advisorReviewReasons = goalId ? (ctx.markers.advisorReviewReasons?.get(goalId) ?? []) : [];
  const advisorReviewMessages = [
    ...new Set(
      advisorReviewReasons.length > 0
        ? advisorReviewReasons.map((reason) => advisorReviewReasonText(reason, userLanguage))
        : [advisorReviewReasonText(undefined, userLanguage)],
    ),
  ];

  // QL-217 — components in a hidden block or a hidden tab.
  const layoutConditionData = {
    formValues: ctx.formValues,
    goals: ctx.goals,
    fieldTypes: ctx.fieldTypes,
    goalId: conditionGoalId,
  };
  const hiddenByLayout = hiddenLayoutComponentIds(
    page.layouts ?? page.flowPageLayouts ?? [],
    page.components ?? [],
    (rule) => evaluateCondition(rule, layoutConditionData),
  );

  return (
    <>
      {page.title && <h2 className="text-2xl font-semibold mb-2">{page.title}</h2>}
      {page.description && <p className="text-muted-foreground mb-6">{page.description}</p>}

      {showAdvisorReviewBanner && (
        <Alert variant="warning" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {advisorReviewMessages.length === 1 ? (
              advisorReviewMessages[0]
            ) : (
              <ul className="list-disc pl-5 space-y-1">
                {advisorReviewMessages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {(page.components ?? []).map((component: any) => {
          if (hiddenByLayout.has(component.id)) return null;

          let conditionalDisplay: ConditionalDisplayConfig | null = null;
          try {
            const config = component.config ? JSON.parse(component.config) : null;
            conditionalDisplay = config?.conditionalDisplay || null;
          } catch {
            // Invalid config
          }

          if (
            !evaluateCondition(conditionalDisplay, {
              formValues: ctx.formValues,
              goals: ctx.goals,
              fieldTypes: ctx.fieldTypes,
              goalId: conditionGoalId,
            })
          ) {
            return null;
          }

          return (
            <div key={component.id}>
              <PublicFlowComponentRenderer
                component={component}
                isCompleted={ctx.isCompleted}
                language={ctx.language}
                flowInstanceId={ctx.flowInstanceId}
                riskModelId={ctx.riskModelId}
                portfolioCollectionId={ctx.portfolioCollectionId}
                flowCurrency={ctx.flowCurrency}
                primaryColor={ctx.primaryColor}
                productTypes={ctx.productTypes}
                goalTypes={ctx.goalTypes}
                multiGoal={ctx.multiGoal}
                maxGoals={ctx.maxGoals}
                portfolioCosts={ctx.portfolioCosts}
                currentGoal={activeGoal}
                currentGoalId={goalId}
                clientType={ctx.clientType}
                clientName={ctx.clientName}
                chartColors={ctx.chartColors}
                onCustomPortfolioSaveComplete={async () => {}}
                scope={page.pageType === "global" ? "global" : "goal"}
                showValidationErrors={ctx.showValidationErrors}
                markers={ctx.markers}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
